import {
  pointLabels,
  reduceRacquet
} from '../../../shared/racquetScoreDomain.mjs'

const failure = (code) => Object.assign(new Error(code), { code })
export const racquetCardDraftKey = ({ clubCode, memberIdentity, matchId }) =>
  `privana:racquet-card:v1:${[clubCode, memberIdentity, matchId].map(encodeURIComponent).join(':')}`
export const cardPointMode = (rows) => {
  const final = rows.at(-1)
  return final && !final.completed && final.score.every((n) => n === '6')
    ? 'tieBreak'
    : 'ordinary'
}
export function createCardDraft(match) {
  const s = match.state,
    racket = ['tennis', 'padel'].includes(s.rules.sport)
  const rows = [
    ...s.segments.map((seg) => ({
      score: seg.score.map(String),
      completed: true
    })),
    { score: (racket ? s.games : s.points).map(String), completed: false }
  ]
  return {
    version: 1,
    baseRevision: match.revision,
    baseEventId: match.events.at(-1)?.id || null,
    rows,
    points: racket ? pointLabels(s) : ['0', '0'],
    pointMode: s.tieBreak ? 'tieBreak' : 'ordinary',
    baseValues: {
      rows: structuredClone(rows),
      points: racket ? pointLabels(s) : ['0', '0'],
      pointMode: s.tieBreak ? 'tieBreak' : 'ordinary'
    },
    ack: false,
    reconcileRevision: null
  }
}
export function ordinaryPointsToInternal(labels) {
  const values = { 0: 0, 15: 1, 30: 2, 40: 3, AD: 4 }
  if (
    !Array.isArray(labels) ||
    labels.length !== 2 ||
    labels.some((v) => !Object.hasOwn(values, v)) ||
    labels.some((v, i) => v === 'AD' && labels[1 - i] !== '40')
  )
    throw failure('point_combination_invalid')
  return labels.map((v) => values[v])
}
export function cardDraftOperation(
  draft,
  match,
  { finish = false, reason, review = false } = {}
) {
  const racket = ['tennis', 'padel'].includes(match.rules.sport)
  if (
    (draft.baseRevision !== match.revision ||
      (draft.baseEventId ?? null) !== (match.events.at(-1)?.id || null)) &&
    !(
      draft.reconcileRevision === match.revision &&
      (draft.reconcileEventId ?? null) === (match.events.at(-1)?.id || null)
    )
  )
    throw failure('draft_revision_changed')
  if (!review && match.events.length && !draft.ack)
    throw failure('draft_consent_required')
  const number = (v) => {
    if (
      typeof v !== 'string' ||
      !/^\d+$/.test(v) ||
      !Number.isSafeInteger(Number(v))
    )
      throw failure('invalid_segment_score')
    return Number(v)
  }
  const hasPoints = draft.points.some((n) => n !== '0')
  if (racket && draft.rows.at(-1)?.completed && hasPoints)
    throw failure('completed_segment_has_points')
  if (racket && hasPoints && cardPointMode(draft.rows) !== draft.pointMode)
    throw failure('point_mode_changed')
  const operation = {
    type: 'direct_score',
    segments: draft.rows.map((row) => ({
      score: row.score.map((value) => {
        try {
          return number(value)
        } catch (e) {
          e.rowIndex = draft.rows.indexOf(row)
          throw e
        }
      }),
      completed: row.completed
    })),
    ...(racket
      ? {
          currentPoints:
            draft.pointMode === 'tieBreak'
              ? draft.points.map(number)
              : ordinaryPointsToInternal(draft.points)
        }
      : {}),
    discardFollowing: true,
    reason,
    ...(finish ? { finishSession: true } : {})
  }
  try {
    const after = reduceRacquet(match.state, operation)
    return { operation, after }
  } catch (error) {
    for (let i = 0; i < operation.segments.length; i++) {
      try {
        reduceRacquet(match.state, {
          ...operation,
          segments: operation.segments.slice(0, i + 1),
          currentPoints: racket ? [0, 0] : undefined,
          finishSession: undefined
        })
      } catch {
        error.rowIndex = i
        break
      }
    }
    throw error
  }
}

// Compare scoring meaning, excluding focus, consent and reconciliation metadata.
export function cardDraftHasChanges(draft, match) {
  if (!draft) return false
  if (draft.pendingEventId) return true
  const base =
    draft.baseValues ||
    (draft.baseRevision === match.revision &&
    (draft.baseEventId ?? null) === (match.events.at(-1)?.id || null)
      ? createCardDraft(match)
      : null)
  if (!base) return true // Older drafts cannot be silently reconciled.
  const normal = (value) =>
    /^\d+$/.test(value) ? String(Number(value)) : value
  const score = (value) =>
    JSON.stringify({
      rows: value.rows.map((row) => ({
        score: row.score.map(normal),
        completed: row.completed
      })),
      points: value.points.map(normal),
      pointMode: value.pointMode
    })
  return score(draft) !== score(base)
}

// Consent/review scope excludes voice tokens and UI-only metadata.
export const cardDraftFingerprint = (draft) =>
  JSON.stringify({
    rows: draft.rows,
    points: draft.points,
    pointMode: draft.pointMode,
    baseRevision: draft.baseRevision,
    baseEventId: draft.baseEventId,
    reconcileRevision: draft.reconcileRevision,
    reconcileEventId: draft.reconcileEventId
  })
